import React from 'react';
import { Form, FormInstance, InputNumber, Radio, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import usePickList from 'hooks/usePickList';
import { PickListValue } from 'types/PickList';
import { FormProps } from 'antd/es/form/Form';
import { projectUsages } from 'const/project';

export interface ProjectInformationsFormType {
  isAncien: 'oui' | 'non';
  nature: string;
  usage: string;
  operation: string;
  surfaceVentileeRehabilitee: number;
  ancienneEnergieChauffage: string;
  nouvelleEnergieChauffage: string;
  surfaceChauffee: number;
  surfaceChauffeeRehabilitee: number;
}

interface ProjectInformationsFormProps extends FormProps<ProjectInformationsFormType> {
  formInstance: FormInstance<ProjectInformationsFormType>;
}

export default function ProjectInformationsForm({
  formInstance,
  ...props
}: ProjectInformationsFormProps) {
  const { t } = useTranslation('add_dossier_project');

  const operation = Form.useWatch('operation', formInstance);

  const { data: projectNatures } = usePickList(
    'dossiers',
    'chantier.natureTravaux',
  );
  // Le metier veut une liste statique pour le moment, NE PAS UTILISER CETTE LISTE
  // const { data: a } = usePickList('dossiers', 'site.usage');
  const { data: projectOperations } = usePickList('dossiers', 'typeOperation');
  const { data: newProjectEnergies } = usePickList(
    'dossiers',
    'batiment.nouvelleEnergieChauffage',
  );
  const { data: previewsProjectEnergies } = usePickList(
    'dossiers',
    'batiment.ancienneEnergieChauffage',
  );

  return (
    <Form<ProjectInformationsFormType>
      form={formInstance}
      layout={'vertical'}
      // className={'blue-form'}
      {...props}
    >
      <Form.Item<ProjectInformationsFormType>
        name={'isAncien'}
        label={t('isAncien')}
        rules={[{ required: true }]}
        required
      >
        <Radio.Group>
          <Radio value="oui">{t('global:yes')}</Radio>
          <Radio value="non"> {t('global:no')} </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item<ProjectInformationsFormType>
        name={'nature'}
        label={t('nature')}
        rules={[{ required: true }]}
        required
      >
        <Select placeholder={t('nature_placeholder')}>
          {projectNatures?.map(({ label, value }: PickListValue) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item<ProjectInformationsFormType>
        rules={[{ required: true }]}
        required
        name={'usage'}
        label={t('usage')}
      >
        <Select placeholder={t('usage_placeholder')}>
          {projectUsages?.map(({ label, value }: PickListValue) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item<ProjectInformationsFormType>
        name={'operation'}
        label={t('operation')}
        rules={[{ required: true }]}
        required
      >
        <Select placeholder={t('operation_placeholder')}>
          {projectOperations?.map(({ label, value }: PickListValue) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {operation === 'Ventilation' && (
        <Form.Item<ProjectInformationsFormType>
          name={'surfaceVentileeRehabilitee'}
          label={t('surfaceVentileeRehabilitee')}
        >
          <InputNumber className={'w-full'} min={0} />
        </Form.Item>
      )}
      {operation === 'Chauffage' && (
        <>
          <Form.Item<ProjectInformationsFormType>
            name={'ancienneEnergieChauffage'}
            label={t('ancienneEnergieChauffage')}
          >
            <Select placeholder={t('ancienneEnergieChauffage_placeholder')}>
              {previewsProjectEnergies?.map(
                ({ label, value }: PickListValue) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ),
              )}
            </Select>
          </Form.Item>
          <Form.Item<ProjectInformationsFormType>
            name={'nouvelleEnergieChauffage'}
            label={t('nouvelleEnergieChauffage')}
          >
            <Select placeholder={t('nouvelleEnergieChauffage_placeholder')}>
              {newProjectEnergies?.map(({ label, value }: PickListValue) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item<ProjectInformationsFormType>
            name={'surfaceChauffee'}
            label={t('add_dossier_project:surfaceChauffee')}
          >
            <InputNumber className={'w-full'} min={0} />
          </Form.Item>
          <Form.Item<ProjectInformationsFormType>
            name={'surfaceChauffeeRehabilitee'}
            label={t('add_dossier_project:surfaceChauffeeRehabilitee')}
          >
            <InputNumber className={'w-full'} min={0} />
          </Form.Item>
        </>
      )}
    </Form>
  );
}
